import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { BaseHttpService } from '../base-http.service';
import { SnackbarService } from '../snackbar/snackbar.service';

/**
 * Response interface for single file import
 */
export interface FileImportResult {
  importedCount: number;
  skippedCount: number;
  failedCount: number;
}

/**
 * Service to manage Parquet import operations and state.
 */
@Injectable({
  providedIn: 'root',
})
export class ParquetImportService extends BaseHttpService {
  private readonly isImportingSubject = new BehaviorSubject<boolean>(false);
  public readonly isImporting$ = this.isImportingSubject.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly snackbarService: SnackbarService,
  ) {
    super();
    // Increase timeout for import operations as they may take longer
    this.defaultTimeout = 300000; // 5 minutes
  }

  /**
   * Imports all parquet files from the GCS bucket.
   * @param useEmulator Whether to use the Firestore emulator
   * @returns Observable of HTML response
   */
  importAll(useEmulator = false): Observable<string> {
    this.isImportingSubject.next(true);
    const endpoint = useEmulator
      ? '/parquet-import/import-all-emulator'
      : '/parquet-import/import-all';
    return this.applyHttpOperators(
      this.http.post(
        environment.backend_host + endpoint,
        {},
        {
          responseType: 'text',
        },
      ),
    ).pipe(
      tap(() => {
        this.isImportingSubject.next(false);
        this.snackbarService.show('Import completed successfully');
      }),
      catchError((error) => {
        this.isImportingSubject.next(false);
        this.snackbarService.show(
          `Import failed: ${error.message || 'Unknown error'}`,
          5000,
        );
        return throwError(() => error);
      }),
    );
  }

  /**
   * Imports parquet files filtered by prefix.
   * @param prefix Optional prefix to filter files
   * @param useEmulator Whether to use the Firestore emulator
   * @returns Observable of HTML response
   */
  importWithPrefix(prefix?: string, useEmulator = false): Observable<string> {
    this.isImportingSubject.next(true);
    const endpoint = useEmulator
      ? '/parquet-import/import-emulator'
      : '/parquet-import/import';
    let params = new HttpParams();
    if (prefix) {
      params = params.set('prefix', prefix);
    }
    return this.applyHttpOperators(
      this.http.post(
        environment.backend_host + endpoint,
        {},
        {
          params,
          responseType: 'text',
        },
      ),
    ).pipe(
      tap(() => {
        this.isImportingSubject.next(false);
        this.snackbarService.show('Import completed successfully');
      }),
      catchError((error) => {
        this.isImportingSubject.next(false);
        this.snackbarService.show(
          `Import failed: ${error.message || 'Unknown error'}`,
          5000,
        );
        return throwError(() => error);
      }),
    );
  }

  /**
   * Imports a single parquet file.
   * @param objectName The name/path of the parquet file
   * @returns Observable of FileImportResult
   */
  importFile(objectName: string): Observable<FileImportResult> {
    this.isImportingSubject.next(true);
    const params = new HttpParams().set('objectName', objectName);
    return this.applyHttpOperators(
      this.http.post<FileImportResult>(
        environment.backend_host + '/parquet-import/import-file',
        {},
        { params },
      ),
    ).pipe(
      tap((result) => {
        this.isImportingSubject.next(false);
        const message =
          result.failedCount > 0
            ? `Import completed with ${result.failedCount} failures`
            : 'File imported successfully';
        this.snackbarService.show(message);
      }),
      catchError((error) => {
        this.isImportingSubject.next(false);
        this.snackbarService.show(
          `Import failed: ${error.message || 'Unknown error'}`,
          5000,
        );
        return throwError(() => error);
      }),
    );
  }

  /**
   * Lists all parquet files in the bucket.
   * @param prefix Optional prefix to filter files
   * @returns Observable of list of file names
   */
  listFiles(prefix?: string): Observable<string[]> {
    let params = new HttpParams();
    if (prefix) {
      params = params.set('prefix', prefix);
    }
    return this.applyHttpOperators(
      this.http.get<string[]>(
        environment.backend_host + '/parquet-import/list',
        {
          params,
        },
      ),
    );
  }

  /**
   * Gets the current importing state.
   */
  get isImporting(): boolean {
    return this.isImportingSubject.value;
  }
}
